import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "../../../redux/store";
import { ApiError } from "./types/common-types";
import {
  DGCoinProviderState,
  DGCoinProviderResponse,
  DGCoinProvider,
} from "./types/dgcoin-types";
import { PaginationReturn, TextStatusSearchParams } from "./types/types";

const initialState: DGCoinProviderState = {
  dgcoinProviders: [],
  allDgcoinProviders: [],
  dgcoinProvider: null,
  selectedDGCoinProvider: null,
  loading: true,
  allLoading: false,
  error: null,
  allError: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

const API_URL = process.env.REACT_APP_API_URL;

export const createDGCoinProvider = createAsyncThunk<
  DGCoinProviderResponse,
  FormData | Omit<DGCoinProvider, "id" | "created_at" | "updated_at">,
  { rejectValue: ApiError; state: RootState }
>("dgcoinProviders/create", async (dgcoinProviderData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = dgcoinProviderData instanceof FormData;
    const res = await axios.post(
      `${API_URL}/dg-coins-providers`,
      dgcoinProviderData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      },
    );
    return {
      message: res.data.message,
      dgcoinProvider: res.data.data,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create DG Coin provider" },
    );
  }
});

export const fetchDGCoinProviders = createAsyncThunk<
  DGCoinProviderResponse & PaginationReturn,
  TextStatusSearchParams,
  { rejectValue: ApiError; state: RootState }
>("dgcoinProviders/fetchAll", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const {
      url,
      perPage = 30,
      search,
      status,
      category,
      country,
      sort_by,
      sort_order,
      ...queryParams
    } = urlFilters || {};

    const headers = { Authorization: `Bearer ${token}` };

    let res;

    if (url) {
      res = await axios.get(url, { headers });
    } else {
      const params: Record<string, any> = {};

      if (search) params.search = search;
      if (status) params.status = status;
      if (queryParams.page) params.page = queryParams.page;
      if (perPage) params.per_page = perPage;
      if (category) params.category = category;
      if (country) params.country = country;
      if (sort_by) params.sort_by = sort_by;
      if (sort_order) params.sort_order = sort_order;

      res = await axios.get(`${API_URL}/dg-coins-providers`, {
        params,
        headers,
      });
    }

    return {
      message: res.data.data.message,
      dgcoinProviders: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch DG Coin providers" },
    );
  }
});

export const fetchAllDGCoinProviders = createAsyncThunk<
  DGCoinProvider[],
  void,
  { rejectValue: ApiError; state: RootState }
>("dgcoinProviders/fetchAllList", async (_, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const url = new URL(`${API_URL}/dg-coins-providers`);
    url.searchParams.set("per_page", "1000");
    const res = await axios.get(url.toString(), {
      headers: { Authorization: `Bearer ${token}` },
    });
    const payload = res.data?.data;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch DG Coin providers" },
    );
  }
});

export const fetchDGCoinProviderById = createAsyncThunk<
  DGCoinProvider,
  number,
  { rejectValue: ApiError; state: RootState }
>("dgcoinProviders/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/dg-coins-providers/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch DG Coin provider" },
    );
  }
});

export const updateDGCoinProvider = createAsyncThunk<
  DGCoinProvider,
  { id: number; data: FormData | Partial<DGCoinProvider> },
  { rejectValue: ApiError; state: RootState }
>("dgcoinProviders/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = data instanceof FormData;
    const res = await axios.post(
      `${API_URL}/dg-coins-providers/${id}?_method=PUT`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      },
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update DG Coin provider" },
    );
  }
});

export const deleteDGCoinProvider = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("dgcoinProviders/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/dg-coins-providers/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete DG Coin provider" },
    );
  }
});

const dgcoinProviderSlice = createSlice({
  name: "dgcoinProviders",
  initialState,
  reducers: {
    resetDGCoinProviderState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedDGCoinProvider = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createDGCoinProvider.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createDGCoinProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.dgcoinProvider) {
          state.dgcoinProviders.unshift(action.payload.dgcoinProvider);
        }
      })
      .addCase(createDGCoinProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to create DG Coin provider";
      })
      .addCase(fetchDGCoinProviders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDGCoinProviders.fulfilled, (state, action) => {
        state.loading = false;
        state.dgcoinProviders = action.payload.dgcoinProviders || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchDGCoinProviders.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch DG Coin providers";
      })
      .addCase(fetchAllDGCoinProviders.pending, (state) => {
        state.allLoading = true;
        state.allError = null;
      })
      .addCase(fetchAllDGCoinProviders.fulfilled, (state, action) => {
        state.allLoading = false;
        state.allDgcoinProviders = action.payload || [];
      })
      .addCase(fetchAllDGCoinProviders.rejected, (state, action) => {
        state.allLoading = false;
        state.allError =
          action.payload?.message || "Failed to fetch DG Coin providers";
      })
      .addCase(fetchDGCoinProviderById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedDGCoinProvider = null;
      })
      .addCase(fetchDGCoinProviderById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedDGCoinProvider = action.payload;
      })
      .addCase(fetchDGCoinProviderById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch DG Coin provider";
      })
      .addCase(updateDGCoinProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateDGCoinProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.dgcoinProviders = state.dgcoinProviders.map((provider) =>
          provider.id === action.payload.id ? action.payload : provider,
        );
        if (state.selectedDGCoinProvider?.id === action.payload.id) {
          state.selectedDGCoinProvider = action.payload;
        }
      })
      .addCase(updateDGCoinProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to update DG Coin provider";
      })
      .addCase(deleteDGCoinProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteDGCoinProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.dgcoinProviders = state.dgcoinProviders.filter(
          (provider) => provider.id !== action.payload,
        );
      })
      .addCase(deleteDGCoinProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to delete DG Coin provider";
      });
  },
});

export const { resetDGCoinProviderState } = dgcoinProviderSlice.actions;
export default dgcoinProviderSlice.reducer;
